import { ActionMenu, CopyableText } from '@ijoin/components';
import { useGetManagedAccountMethodologyByIdSuspense } from '@ijoin/hooks';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Card,
  CardContent,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import {
  createFileRoute,
  Link,
  Outlet,
  useLocation,
  useNavigate
} from '@tanstack/react-router';

import { useToggle } from 'usehooks-ts';

import { MethodologyMetricTiles, QueryBoundary } from '../../components';
import { MethodologyFormDialog } from '../../components/MethodologyFormDialog';

const MethodologyDetailPage = () => {
  const params = Route.useParams();
  const location = useLocation();
  const navigate = useNavigate({ from: Route.fullPath });

  const [isEditOpen, toggleEditOpen, setIsEditOpen] = useToggle();

  const methodology = useGetManagedAccountMethodologyByIdSuspense(
    params.managedAccountMethodologyId
  );

  const onEditSuccess = () => {
    setIsEditOpen(false);
    methodology.refetch();
  };

  const onNavigateAssociations = () =>
    navigate({
      to: '/methodologies/$managedAccountMethodologyId/associations'
    });

  const onNavigateProviders = () =>
    navigate({
      to: '/methodologies/$managedAccountMethodologyId/managed-account-providers'
    });

  return (
    <Stack spacing={2} sx={{ height: '100%', p: 2 }}>
      <Link
        data-testid='backToMethodologies'
        style={{ alignItems: 'center', display: 'flex', gap: 4 }}
        to='/methodologies'>
        <ArrowBackIcon fontSize='small' />
        Methodologies
      </Link>

      <Card sx={{ p: 1 }}>
        <CardContent>
          <Stack
            alignItems='center'
            direction='row'
            justifyContent='space-between'>
            <Stack
              alignItems='center'
              direction='row'
              spacing={3}
              sx={{ flexWrap: 'wrap' }}>
              <Stack>
                <Typography color='text.secondary' variant='overline'>
                  Name
                </Typography>
                <Typography data-testid='methodologySummaryName' variant='h5'>
                  {methodology.data.managedAccountMethodologyName}
                </Typography>
              </Stack>
              <Stack>
                <Typography color='text.secondary' variant='overline'>
                  Methodology ID
                </Typography>
                <Stack alignItems='center' direction='row' spacing={0.5}>
                  <Typography sx={{ fontFamily: 'monospace' }} variant='body2'>
                    {params.managedAccountMethodologyId.slice(0, 8)}…
                  </Typography>
                  <CopyableText value={params.managedAccountMethodologyId} />
                </Stack>
              </Stack>
            </Stack>

            <ActionMenu
              actions={[{ content: 'Edit', onSelect: toggleEditOpen }]}
              trigger={
                <IconButton data-testid='methodologyActionsMenuButton'>
                  <MoreVertIcon />
                </IconButton>
              }
            />
          </Stack>
        </CardContent>
      </Card>

      <MethodologyMetricTiles
        managedAccountMethodologyId={params.managedAccountMethodologyId}
      />

      <Tabs
        value={
          location.pathname.endsWith('/managed-account-providers')
            ? 'managed-account-providers'
            : 'associations'
        }>
        <Tab
          data-testid='tabAssociations'
          label='Associations'
          onClick={onNavigateAssociations}
          value='associations'
        />
        <Tab
          data-testid='tabManagedAccountProviders'
          label='Managed Account Providers'
          onClick={onNavigateProviders}
          value='managed-account-providers'
        />
      </Tabs>

      <Outlet />

      {isEditOpen && (
        <MethodologyFormDialog
          isOpen={isEditOpen}
          methodology={methodology.data}
          onClose={toggleEditOpen}
          onSuccess={onEditSuccess}
        />
      )}
    </Stack>
  );
};

export const Route = createFileRoute(
  '/methodologies/$managedAccountMethodologyId'
)({
  component: () => (
    <QueryBoundary>
      <MethodologyDetailPage />
    </QueryBoundary>
  )
});
